import {CSSProperties} from 'react'

import Colors from '@utils/Colors.js'

// All text components (H1..H6, S1, S2, P, Caption) take their look from here: one entry per variant and the text
// colours. Change a value here instead of in a component. Headings have no outer margins: the space around them
// comes from the caller's style or layout (Stack, Row).

export type TextVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 's1' | 's2' | 'p' | 'caption'

export const typography: Record<TextVariant, CSSProperties> = {
    h1: {fontSize: 96, fontWeight: 300, letterSpacing: -1.5},
    h2: {fontSize: 60, fontWeight: 300, letterSpacing: -0.5},
    h3: {fontSize: 28, fontWeight: 400, letterSpacing: 0},
    h4: {fontSize: 22, fontWeight: 400, letterSpacing: 0},
    h5: {fontSize: 14, fontWeight: 700, letterSpacing: 0.1},
    h6: {fontSize: 14, fontWeight: 500, letterSpacing: 0.1},
    // Subtitles: S1 for titles in lists and cards, S2 for small grey labels
    s1: {fontSize: 14, fontWeight: 500, letterSpacing: 0.1, lineHeight: 1.5},
    s2: {fontSize: 12, fontWeight: 500, letterSpacing: 0.1},
    // Body text, long words break so they don't overflow their box
    p: {fontSize: 14, fontWeight: 400, letterSpacing: 0.4, lineHeight: 1.5, wordBreak: 'break-word'},
    caption: {fontSize: 12, fontWeight: 400, letterSpacing: 0.4, lineHeight: '16px'}
}

export const textColors = {
    dark: Colors.textDark,
    medium: Colors.textMedium,
    // Text on a dark background
    onDark: Colors.white,
    disabled: Colors.textMedium
}

// S2 and Caption are grey by default, the rest dark
const defaultColors: Partial<Record<TextVariant, string>> = {
    s2: textColors.medium,
    caption: textColors.medium
}

// Space between the icon of a text (P icon) and the text
export const textIconGap = 12

// One line that ends in ... when it doesn't fit
export const ellipsisStyle: CSSProperties = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
}

interface TextColorState {
    variant: TextVariant
    disabled?: boolean
    /** On a dark background, white text */
    onDark?: boolean
}

/** Text colour of a variant: disabled wins over onDark, the caller's style colour wins over both */
export function getTextColor({variant, disabled, onDark}: TextColorState): string {
    if (disabled) {
        return textColors.disabled
    }

    if (onDark) {
        return textColors.onDark
    }

    return defaultColors[variant] || textColors.dark
}
